<template>
  <div
    v-if="question"
    :class="[`QuestionLayout-${screen.layout.type}`, 'QuestionRenderer']"
  >
    <div
      :class="[{'Selected' : isSelected}, 'Question-container']"
      @click="handleSelectQuestion"
    >
      <div class="Question-inputs">
        <div
          v-if="screen.layout.type !== 'text-only'"
          class="MediaSection"
        >
          <template v-if="!question.layout.image && screen.layout.type !== 'media-middle'">
            <label
              class="MediaUpload-wrapper"
              :for="`${question.vuexId}-MediaUpload`"
            >
              <span class="Icon-s Icon-cloud-upload-alt" />
              Image Upload
              <span class="Hint">Max 10mb 1080px / 1040px</span>
            </label>
          </template>
          <div
            v-if="screen.layout.type === 'media-middle' && !question.layout.image && !question.layout.videoId"
            class="MediaMiddleSelection-container u-text-center"
          >
            <p class="NoMedia-placeholder">
              Add an image or a video. Media will embed here
            </p>
          </div>
          <div
            v-if="question.layout.videoId && question.layout.type === 'media-middle'"
            class="LayoutVideo"
          >
            <p>
              <span style="text-transform: capitalize">{{ question.layout.videoSource }}</span>
              video
              id
              <a
                v-if="question.layout.videoUrl"
                target="_blank"
                :href="question.layout.videoUrl"
              >{{ question.layout.videoId }}</a>

              <span v-else>{{ question.layout.videoId }}</span>
              embed
            </p>
          </div>
          <div
            v-else-if="question.layout.image"
            class="LayoutImage"
          >
            <img
              :src="question.layout.image"
              alt=""
            >
          </div>
        </div>
        <div class="Question-input-type">
          <div class="u-flex">
            <label
              v-if="!hideQuestionNumberLabel"
              class="u-flex"
            >
              <span class="QuestionNumber">{{ questionNumber }}</span>
              <span
                :class="['DynamicLabel', { 'Required': question.is_required }]"
                v-html="questionLabel"
              />
            </label>

            <h2
              v-else-if="question.type === 'header'"
              class="HeaderText"
            >
              {{ question.label }}
            </h2>

            <p
              v-else-if="question.type === 'paragraph'"
              class="ParagraphText"
            >
              {{ question.label }}
            </p>

            <template
              v-else-if="question.type === 'statement'"
            >
              <span class="QuestionNumber">S{{ statementNumber }}</span>
              <div
                :class="[`${question.text_align}`, 'StatementText']"
                v-html="questionLabel"
              />
            </template>

            <tooltip v-if="questionHasConditionalLogic">
              <span
                slot="trigger"
                class="Table-action"
              >
                <span
                  slot="trigger"
                  class="ConditionalLogicIcon Icon-s Icon-ballot-check"
                />
              </span>
              <span
                slot="tip"
              > Shown if {{ conditionalLogicFieldLabel }}
                <span>{{ question.conditional_logic.constraint }}</span>
                <span v-if="question.conditional_logic.value"> {{ question.conditional_logic.value }}</span>
              </span>
            </tooltip>
          </div>
          <div
            v-if="!isSingleSelectionCheckbox || customYesNoCheckboxType"
            class="Description"
            v-html="question.hint"
          />

          <!-- QUESTION TYPES:Start -->
          <rmp-input
            v-if="question.type === 'text'"
            :model-data="question.value"
            :placeholder="question.placeholder"
          />

          <rmp-input
            v-else-if="question.type === 'email'"
            input-type="email"
            :model-data="question.value"
            :placeholder="question.placeholder"
          />

          <rmp-input
            v-else-if="question.type === 'tel'"
            :model-data="question.value"
            :placeholder="question.placeholder"
          />

          <rmp-textarea
            v-else-if="question.type === 'long_text'"
            class="TextArea"
            :model-data="question.value"
            :placeholder="question.placeholder"
          />

          <rmp-input
            v-else-if="question.type === 'number'"
            input-type="number"
            :placeholder="question.placeholder"
          />

          <rmp-input
            v-else-if="question.type === 'url'"
            :model-data="question.value"
            :placeholder="question.placeholder"
          />

          <div
            v-else-if="question.type === 'radio'"
            class="radio-button-group u-flex u-flex-wrap"
          >
            <div
              v-for="(radio, index) in radioOptions"
              :key="index"
            >
              <rmp-radio-button
                :label="radio.label"
                :value="false"
                name="radioName"
              />
            </div>
          </div>

          <div
            v-else-if="question.type === 'checkbox' ||
              question.type === 'checkboxOptGroup'"
          >
            <template v-if="customCheckboxUuids.includes(question.question_uuid)">
              <ul>
                <li class="YesLabel">
                  Yes
                </li>
                <li class="NoLabel">
                  No
                </li>
              </ul>
            </template>
            <template
              v-else-if="isSingleSelectionCheckbox"
            >
              <rmp-checkbox
                v-model="checkboxModel"
                :html-label="question.label"
                :value="question.value"
                :required="question.is_required"
              />
              <div
                class="Description"
                v-html="question.hint"
              />
            </template>
            <div
              v-for="(checkbox, index) in questionOptionsList"
              v-else
              :key="index"
            >
              <rmp-checkbox
                :label="checkbox.label"
                :checked="false"
                :value="false"
              />
            </div>
          </div>

          <rmp-select
            v-else-if="question.type === 'select'"
            class="Form-field--select"
            :items="selectFieldOptions"
            :model-data="question.value"
          />

          <rmp-star-rating
            v-else-if="question.type === 'rating'"
            :max-stars="question.options.range"
            :polar-adjectives="{
              good: question.options.max_rating_label,
              bad: question.options.min_rating_label
            }"
          />

          <rmp-autocomplete
            v-else-if="question.type === 'autocomplete'"
            class="Form-field--autocomplete"
            :items="question.options || []"
            :placeholder="question.value || null"
            option-key="label"
          />

          <div v-else-if="question.type === 'ranking'">
            <ul class="RankingOrderList">
              <li
                v-for="(option, index) in question.options"
                :key="option.label"
              >
                <span class="RankingOrderNumber">{{ index + 1 }}</span> {{ option.label }}
              </li>
            </ul>
          </div>

          <div v-else-if="question.type === 'multi_choice_image'">
            <div class="MultiChoiceImage-container u-flex u-flex-wrap">
              <div
                v-for="(image, index) in question.options"
                :key="index"
                class="Images-container"
              >
                <figure>
                  <img
                    :src="image.value"
                    alt=""
                  >
                </figure>
                <p v-show="question.is_label_hidden === false">
                  {{ image.label }}
                </p>
              </div>
              <label
                class="MediaUpload-wrapper"
                :for="`${question.vuexId}-MultiImageUploader`"
              >
                <span class="Icon-s Icon-cloud-upload-alt" />
                Add Image
                <span class="Hint">jpeg,jpg,png,gif</span>
                <span class="Hint">max width: 300px</span>

              </label>
              <input
                :id="`${question.vuexId}-MultiImageUploader`"
                class="MediaUploader"
                type="file"
                name="logoFilename"
                accept="image/*"
                @change="handleUploadMultiChoiceImageSelection($event)"
              >
            </div>
          </div>

          <div v-else-if="question.type === 'file_upload'">
            <div class="FileUpload-container">
              <span class="Icon-s Icon-file-upload" />
              <p>Drag and drop or click here to upload a File</p>
              <p>Max file size is: {{ question.options.max_file_size_in_mb }}</p>
            </div>
            <div style="display: flex;align-items: stretch; ">
              <div style="margin-right: 10px;">
                Info:
              </div>
              <div style="margin-right: 10px;">
                <span class="Icon-s Icon-file-word" /> Documents: {{ question.options.allows_documents ? 'Yes' : 'No' }}
              </div>
              <div style="margin-right: 10px;">
                <span class="Icon-s Icon-camera" /> Images: {{ question.options.allows_images ? 'Yes' : 'No' }}
              </div>
              <div style="margin-right: 10px;">
                <span class="Icon-s Icon-file-archive" /> Zip (Compressed files): {{ question.options.allows_zips ? 'Yes' : 'No' }}
              </div>
            </div>
          </div>

          <rmp-datepicker
            v-else-if="question.type === 'date'"
            :model-data="question.value"
            :disable-dates="{from: null}"
          />

          <div
            v-else-if="question.type === 'time'"
            class="Form-group Time"
          >
            <rmp-select
              :model-data="timeDefaultValue.hour"
              :items="getNumberArray(1,23,1)"
              style="width: 100px; margin-right: 10px;"
              label="Hour"
            />
            <rmp-select
              :model-data="timeDefaultValue.minute"
              :items="getNumberArray(0,55,5)"
              style="width: 100px"
              label="Minutes"
            />
          </div>

          <div
            v-if="screen.layout.type !== 'media-middle'"
            :class="['NextQuestionButton-container', {'Selected': nextQuestionButtonSelected}, question.text_align]"
          >
            <button
              class="Button"
              @click="handleSelectNextQuestionButton(question)"
            >
              {{ question.go_to_next_question_button_label }}
            </button>
          </div>
        </div>
        <div
          v-if="screen.layout.type === 'media-middle'"
          class="NextQuestionButton-container"
          :class="[{'Selected': nextQuestionButtonSelected}, question.text_align]"
        >
          <button
            class="Button Button--green"
            @click="handleSelectNextQuestionButton(question)"
          >
            {{ question.go_to_next_question_button_label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapState, mapActions, mapGetters, mapMutations,
} from 'vuex';
import RmpInput from '@rmpenterprise/form-input';
import RmpSelect from '@rmpenterprise/form-select';
import RmpRadioButton from '@rmpenterprise/form-radio-button';
import RmpCheckbox from '@rmpenterprise/form-checkbox';
import RmpAutocomplete from '@rmpenterprise/form-autocomplete-select';
import RmpDatepicker from '@rmpenterprise/form-date-picker';
import RmpTextarea from '@rmpenterprise/form-text-area';
import RmpStarRating from '@rmpenterprise/star-rating';
import Tooltip from '../../common/tooltip.vue';

export default {
  components: {
    RmpInput,
    RmpRadioButton,
    RmpCheckbox,
    RmpSelect,
    RmpAutocomplete,
    RmpDatepicker,
    RmpTextarea,
    RmpStarRating,
    Tooltip,
  },
  props: {
    question: {
      type: Object,
      default: null,
    },
    screen: {
      type: Object,
      default: null,
    },
    showReplaceQuestion: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      checkboxlocalValue: false,
      customCheckboxUuids: ['email_contact_permitted', 'phone_contact_permitted'],
      mediaVideoUrl: null,
      showAddImage: false,
      nonQuestionElementTypes: ['header', 'paragraph', 'statement'],
      nextQuestionButtonSelected: false,
      matchedVariableQuestionNumber: null,
    };
  },
  computed: {
    ...mapState('forms', {
      selectedQuestion: (state) => state.selectedQuestion,
      formQuestions: (state) => state.form.questions,
    }),
    ...mapState('themes', {
      previewThemeCss: (state) => state.previewTheme,
    }),
    ...mapGetters('forms', ['getStatementsFromForm', 'getFormQuestionsWithoutStatements']),
    isSelected() {
      if (this.selectedQuestion.index === undefined) return false;
      return this.selectedQuestion.vuexId === this.question.vuexId;
    },

    /*
      Property to compute 'Smart Tags' currently in the format of ${Q1}
      Smart tags insert a reference to another question answer so that questions can refer to each other
     */
    questionLabel() {
      if (this.question.label.length && this.question.label.match(/\${Q[0-9]}/g)) {
        const questionVariable = this.question.label.match(/\${Q[0-9]}/g)[0];
        const questionNumber = questionVariable.match(/\d+/)[0];

        const questionLabel = this.getFormQuestionsWithoutStatements[questionNumber - 1]
          ? this.getFormQuestionsWithoutStatements[questionNumber - 1].label : null;

        if (!questionLabel) {
          return this.question.label;
        }

        const strippedLabel = this.stripHtmlTags(questionLabel).trim();

        const label = strippedLabel.length > 25
          ? `Q${questionNumber}.${strippedLabel.substring(0, 25)}...` : `Q${questionNumber}.${strippedLabel}`;

        return this.question.label.replace(
          questionVariable,
          `<span class="QuestionReference" style="background: #D0D7E1; padding: 4px; border-radius: 6px; font-size: 1rem">${label}</span>`,
        );
      }

      return this.question.label;
    },

    selectFieldOptions() {
      if (!this.question.options) return [];

      return this.question.options.map((q) => q.label);
    },

    statementNumber() {
      return this.getStatementsFromForm.findIndex((statement) => this.question.vuexId === statement.vuexId) + 1;
    },

    questionNumber() {
      return this.getFormQuestionsWithoutStatements.findIndex((question) => question.vuexId === this.question.vuexId) + 1;
    },

    checkboxModel: {
      get() {
        return !this.checkboxlocalValue;
      },
      set(val) {
        this.checkboxlocalValue = val;
      },
    },

    /*
      Property to compute the question label of a question mapped inside conditional logic
      Note: html_form_input_id is saved as the question uuid for conditional logic mapping
     */
    conditionalLogicFieldLabel() {
      const [mappedQuestion] = this.formQuestions.filter((q) => {
        if (q.is_custom_question) return q.html_form_input_id === this.question.conditional_logic.related_to_question_uuid;
        return q.question_uuid === this.question.conditional_logic.related_to_question_uuid;
      });
      return mappedQuestion.label;
    },
    canReplaceQuestion() {
      return this.showReplaceQuestion && this.question.list_of_questions_that_can_replace.length !== 0;
    },

    hideQuestionNumberLabel() {
      if (!this.question) return true;
      return this.nonQuestionElementTypes.indexOf(this.question.type) > -1
          || (this.isSingleSelectionCheckbox && !this.customYesNoCheckboxType);
    },
    isSingleSelectionCheckbox() {
      return ['checkbox', 'checkboxOptGroup'].indexOf(this.question.type) > -1
          && this.questionOptionsList.length === 0;
    },
    customYesNoCheckboxType() {
      // Fields of question.type === 'checkbox' however the rendering is custom Yes No options
      return this.customCheckboxUuids.includes(this.question.question_uuid);
    },
    questionHasConditionalLogic() {
      if (this.question.conditional_logic) {
        if (Object.keys(this.question.conditional_logic).length === 3) {
          return Object.keys(this.question.conditional_logic).every((key) => this.question.conditional_logic[key] !== null);
        }
      }
      return false;
    },
    questionOptionsList() {
      return this.question.options || [];
    },
    radioOptions() {
      return this.question.options || [{ label: '' }];
    },
    timeDefaultValue() {
      if (this.question.type === 'time') {
        const splitTime = this.question.value ? this.question.value.split(':') : [];

        if (splitTime.length > 0) {
          return { hour: splitTime[0], minute: splitTime[1] };
        }

        return { hour: '', minute: '' };
      }
      return null;
    },
  },
  mounted() {

  },
  methods: {
    ...mapActions('forms', ['removeFormQuestion']),
    ...mapMutations('forms', ['updateSelectedQuestion', 'resetSelectedQuestion']),
    handleSelectQuestion() {
      // Remove custom_property_to_update as we do not want to store a custom_property_to_update inside the question
      const { custom_property_to_update, ...question } = this.question; //eslint-disable-line
      this.$emit('question-renderer:select', question);
    },
    handleSelectNextQuestionButton(question) {
      this.nextQuestionButtonSelected = true;
      const Button = {
        custom_property_to_update: 'go_to_next_question_button_label', ...question,
      };

      this.updateSelectedQuestion(Button);
    },

    stripHtmlTags(string) {
      return string.replace(/(<([^>]+)>)/ig, ' ');
    },

    handleAddVideoUrl() {
      const question = {
        ...this.question,
        layout: { ...this.question.layout, video: this.mediaVideoUrl },
      };
      this.updateSelectedQuestion(question);
    },
    handleRemoveLayoutMedia(type = 'image') {
      const question = {
        ...this.question,
      };
      question.layout[type] = null;

      this.updateSelectedQuestion(question);
    },

    handleUploadMultiChoiceImageSelection({ currentTarget: input }) {
      // Take the uploaded image and convert to a base64 string in order to show a preview in the browser
      // Add this to the event in order for it to be added to the form.
      if (input.files && input.files[0]) {
        const reader = new FileReader();

        reader.onload = (e) => {
          // Multiple image replacement for questions
          const options = this.question.options
            ? [...this.question.options, { label: `Image ${this.question.options.length + 1}`, value: e.target.result }]
            : [{ label: 'Image 1', value: e.target.result }];
          const question = {
            ...this.question,
            options,
          };

          this.updateSelectedQuestion(question);
        };

        reader.readAsDataURL(input.files[0]); // convert to base64 string
      }
    },
    replaceQuestion() {
      this.$emit('question-renderer:replace', this.question);
    },

    removeQuestion(question) {
      this.removeFormQuestion(question);
    },
    getNumberArray(start, end, increment = 1) {
      // Helper function can probably be added to a utilities file or the store.
      // Taken from SMS code base to generate years
      const len = Math.floor((end - start) / increment) + 1;
      // Get numbers and add 0 to any number that's less than 2
      return Array(len).fill().map((_, index) => String(start + (index * increment)).padStart(2, 0));
    },
  },

};
</script>

<style lang="scss" scoped>

::v-deep input::placeholder,
::v-deep textarea::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
  color: #8c8c8c;
  opacity: 1; /* Firefox */
}

.Question-container {
  position: relative;
  padding: 0 80px;

  ul {
    margin: 0;
  }

  .DynamicLabel {
    color: var(--formTextQuestionColor);
   ::v-deep &.Required >:last-child::after {
      content: '*';
      color: red;
      margin-left: 4px;
    }
  }

  .Question-inputs {
    display: flex;
    align-items: center;

    .ParagraphText, .HeaderText {
      max-width: 100%;
      word-break: break-word;
    }
    .ParagraphText {
      padding: 5px 10px;
    }

    .YesLabel, .NoLabel {
      width: 60px;
      background: white;
      font-weight: bold;
      border: 1px solid lightgray;
      padding: 10px 0;
      text-align: center;
      border-radius: 6px;
      margin: 10px 0;
    }

    .RankingOrderList {

      li {
        background: #F7F8F9;
        border: 1px solid #D0D7E1;
        padding: 5px 5px;
        width: 50%;
        border-radius: 6px;
        margin: 10px 0 10px 0;
      }
      .RankingOrderNumber {
        background: #D0D7E1;
        border-radius: 3px;
        padding: 3px 8px;
        color: white;
        margin-right: 4px;
      }

    }

    .Question-input-type {
      color: var(--formTextAnswerColor);
      flex-grow: 1;
      max-height: 415px;

      .Description {
        font-size: 1rem;
        margin: 5px 0;
        color: #6F7A85;
      }

      .QuestionNumber {
        margin-left: -24px;
        margin-right: 8px;
        font-weight: bold;
        color: var(--formTextQuestionColor);
      }

      label {
        font-size: 18px;
        word-break: break-word;
      }
      .ParagraphText, .HeaderText {
        display: inline-block;
      }

      h1,h2,h3, p {
        color: var(--formTextAnswerColor);
        margin: 0;
      }

      ::v-deep p {
        margin: 0;
        color: var(--formTextQuestionColor);
      }

      .HiddenQuestion, .ConditionalLogicIcon {
        color: #47505a7a;
      }

      .BrandingImage {
        position: absolute;
        &.disabled {
          color: lightgray;
          pointer-events: none;
        }
      }
    }
  }

  .Icon.arrow-left {
    display:none;
  }

  select {
    margin-top: -20px;
  }

  .Icon-grip-vertical {
    flex-basis: 20px;
    font-size: 17px;
    align-self: center;
    color: #d3dfe2;
  }
}

.QuestionRenderer {

  .text-align-left {
    text-align: left !important;
  }

  .text-align-right {
    text-align: right !important;
  }

  .text-align-center {
    text-align: center !important;
  }

  .NextQuestionButton-container {
    margin-top: 24px;
    Button {
      border-color: var(--formButtonBorderColour);
      background: var(--formButtonBackground);
      color: var(--formButtonTextColor);
    }
  }

  .FileUpload-container {
    display: flex;
    margin: 20px 0;
    width: 90%;
    background: var(--formBackgroundColor);
    color: var(--formTextQuestionColor);
    border-radius: 4px;
    flex-direction: column;
    align-items: center;
    padding: 35px 10px;
    text-align: center;

    .Icon-s {
      font-size: 1.4rem;
      margin-bottom: 10px;
    }
  }

  .MediaSection {
    position: relative;
    height: 500px;
    width: 50%;
    border-radius: 6px;
    background: #E0E4EA;
    border: 1px solid #E0E4EA;

    .LayoutImage {
      height: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .MediaMiddleSelection-container {
      .NoMedia-placeholder {
        height: 100px;
        transform: translateY(46px);
      }
      .AddMedia-label {
        color: #046FF0;
      }
    }

    .AddImage-container {
      position: relative;

      .MediaUploader {
        display: none;
      }
    }

    .MediaUpload-wrapper {
      top: 200px;
      position: relative;
      display: block;
      text-align: center;
      padding: 10px;

      .Icon-s {
        display: block;
        margin-bottom: 5px;
        font-size: 1.4rem;
      }

      .Hint {
        display: block;
      }
    }

    .MediaUploader {
      visibility: hidden;
    }

    .Media-Container {
      height: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  // MEDIA MIDDLE
  &.QuestionLayout-media-middle {
    .Question-inputs {
      flex-direction: column;
    }
    .Question-input-type {
      width: 100%;
      order:1 ;
      max-height: unset;
    }
    .MediaSection {
      height: auto;
      margin-top: 10px;
      order: 2;
      width: 100%;
    }

    .LayoutVideo {
      height: 200px;

      p {
        text-align: center;
        transform: translateY(75px);
      }
    }

    .LayoutImage {
      height: 140px;

      img {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }
    }

    .MediaUploader {
      left: 6vw;
      top: 30%;
    }
    .NextQuestionButton-container {
      order: 3;
      width: 100%;
    }
  }

  // MEDIA LEFT
  &.QuestionLayout-image-left {
    .Question-container {
      padding: 0;
    }
    .Question-inputs {
      justify-content: flex-end;
    }
    .Question-input-type {
      width: 50%;
      padding: 0 42px;
    }
  }

  // MEDIA RIGHT
  &.QuestionLayout-image-right {
    .Question-container {
      padding: 0;
    }
    .MediaSection {
      order: 1;
    }
    .Question-inputs {
      justify-content: flex-start;
    }
    .Question-input-type {
      width: 50%;
      padding: 0 42px;
    }
    .NextQuestionButton-container {
      text-align: left;
    }
  }
  // BACKGROUND IMAGE
  &.QuestionLayout-background-image {
    .MediaSection {
      position: absolute;
      width: 100%;
      left: 0;
    }
    .MediaUpload-wrapper {
      top: 80%;
    }
    .Question-input-type {
      z-index: 1;
      backdrop-filter: blur(2px);
      background-color: #ffffff1f;
      padding: 26px;
      border-radius: 6px;
    }
  }
}
.MultiChoiceImage-container {
  align-items: baseline;

  .MediaUploader {
    display: none;
  }

  .MediaUpload-wrapper {
    background-color: #E0E4EA;
    border: 1px dotted #47505A;
    cursor:pointer;
    align-self: center;
    font-size: 0.9rem !important;
    text-align: center;
    padding: 16px 8px;
    border-radius: 6px;
    margin-left: 5px;

    .Hint {
      display: block;
      font-size: 0.8rem;
      margin: 0;
    }

    .Icon-s {
      display: block;
      margin-bottom: 2px;
      font-size: 1.4rem;
    }
  }

  .Images-container {
    text-align: center;
    margin: 4px 4px;
    flex-basis: 30%;

    figure {
      border: 1px solid #D0D7E1;
      border-radius: 4px;
      text-align: center;
      padding: 2px;
    }

    img {
      height: 120px;
      vertical-align: middle;
      object-fit: cover;
    }

    input {
      width: 160px;
    }
  }
}

.StatementText {
  flex-grow: 1;
}

::v-deep .Form-field--radio {
  min-width: 135px;
  min-height: 30px;
  border: 1px solid lightgray;
  padding: 4px 19px 4px 11px;
  border-radius: 4px;
  margin: 0 10px 10px 0;

  input {
    display: none;
  }
  label {
    margin: 0;

    &:before, &:after {
      display: none;
    }
  }
}

.QuestionReference {
  background: lightgrey;
  padding: 3px;
  border-radius: 3px;
}

</style>
